import React from 'react';
import { Job } from '../../types/jobs';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Calendar, DollarSign, CheckCircle, Globe, ArrowUpRight, Building2, Clock, ExternalLink, Award, Tag } from 'lucide-react';
import * as amplitude from '@amplitude/analytics-browser';

interface JobCardProps {
  job: Job;
  isPremium?: boolean;
  onClick: (job: Job) => void;
  categoryMap?: Record<string, string>;
}

export function JobCard({ job, isPremium = false, onClick, categoryMap = {} }: JobCardProps) {
  // Format publication date with improved error handling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non spécifiée';
    
    try {
      // First check if the string is a valid date format
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) {
        return 'Non spécifiée';
      }
      return format(parsedDate, 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Non spécifiée';
    }
  };

  // Function to safely decode HTML
  const decodeHTML = (html?: string) => {
    if (!html) return '';
    try {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    } catch (e) {
      console.error('HTML decode error:', e);
      return html || '';
    }
  };

  // Get employer display name
  const employerDisplay = React.useMemo(() => {
    if (typeof job.employer === 'object' && job.employer !== null && !Array.isArray(job.employer)) {
      // Handle the case where employer is an object with a name property
      return job.employer.name || "Employeur non spécifié";
    } else if (Array.isArray(job.employer) && job.employer.length > 0) {
      // Handle the case where employer is an array with content
      if (typeof job.employer[0] === 'string') {
        return job.employer[0];
      } else if (typeof job.employer[0] === 'object' && job.employer[0]?.name) {
        return job.employer[0].name;
      }
    } else if (typeof job.employer === 'string' && job.employer) {
      // Handle the case where employer is a string
      return job.employer;
    }
    
    // Fallback for empty arrays or null/undefined employers
    return "Employeur non spécifié";
  }, [job.employer]);

  // Get category name if available
  const categoryName = React.useMemo(() => {
    if (!job.category) return null;
    
    // Convert category to string for proper lookup
    const categoryId = String(job.category);
    return categoryMap[categoryId] || `Catégorie ${job.category}`;
  }, [job.category, categoryMap]);

  const handleCardClick = () => {
    // Track click before navigation
    amplitude.track('Job Card Click', {
      jobId: job.id,
      jobTitle: job.title || 'Unknown job',
      isPremium: isPremium
    });
    
    // Call the onClick handler passed from parent
    if (typeof onClick === 'function') {
      onClick(job);
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className={`group relative bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md border flex flex-col ${
        isPremium 
          ? 'border-amber-200 dark:border-amber-700/50 transform hover:-translate-y-1 transition-all duration-300'
          : 'border-gray-200/50 dark:border-gray-700/50 transition-all duration-200 hover:-translate-y-1'
      } overflow-hidden cursor-pointer`}
    >
      {/* Premium indicator with improved design */}
      {isPremium && (
        <div className="absolute top-0 right-0 z-10 overflow-hidden rounded-tr-xl">
          <div className="relative">
            {/* Background ribbon */}
            <div className="w-20 h-20 bg-gradient-to-bl from-amber-500 to-amber-600 absolute right-[-10px] top-[-10px] transform rotate-45 shadow-lg"></div>
            {/* Icon wrapper */}
            <div className="absolute top-0 right-0 pt-1.5 pr-1.5">
              <div className="flex items-center justify-center h-8 w-8">
                <Award className="w-4 h-4 text-white drop-shadow-md" />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className={`p-4 sm:p-6 flex-1 flex flex-col ${isPremium ? 'bg-gradient-to-br from-amber-50 to-amber-100/30 dark:from-amber-900/10 dark:to-amber-800/5' : ''}`}>
        {/* Job Title & Verification Badges */}
        <div className="flex items-start justify-between gap-3 mb-3 sm:mb-4">
          <h3 className={`text-base sm:text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 ${isPremium ? 'text-amber-800 dark:text-amber-300' : ''}`}>
            {decodeHTML(job.title)}
          </h3>
          <div className="flex flex-col items-end gap-1 sm:gap-2 ml-1 sm:ml-2 flex-shrink-0">
            {job.employerVerified && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                <CheckCircle className="w-3 h-3" />
                <span>Vérifié</span>
              </span>
            )}
            {isPremium && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                <Award className="w-3 h-3" />
                <span>Premium</span>
              </span>
            )}
            {job.locationFlexible === 1 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                <Globe className="w-3 h-3" />
                <span>Flexible</span>
              </span>
            )}
          </div>
        </div>
        
        {/* Job Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-4 flex-1">
          <div className="col-span-1 sm:col-span-2">
            {/* Employer */}
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
              <Building2 className="w-4 h-4 flex-shrink-0" />
              <span className={`font-medium text-sm sm:text-base ${isPremium ? 'text-amber-700 dark:text-amber-300' : ''}`}>{employerDisplay}</span>
            </div>
          </div>

          {/* Category - NEW */}
          {categoryName && (
            <div className="col-span-1 sm:col-span-2 mb-1">
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Tag className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="line-clamp-1 text-xs sm:text-sm">{categoryName}</span>
              </div>
            </div>
          )}

          <div className="col-span-1">
            {/* Location */}
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 mb-2">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="line-clamp-1 text-xs sm:text-sm">{decodeHTML(job.location)}</span>
            </div>
            {/* Salary */}
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">{job.salaryFull || ''}</span>
            </div>
          </div>

          <div className="col-span-1 sm:col-span-2">
            {/* Start Date */}
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs sm:text-sm">
                {job.startASAP 
                  ? 'Dès que possible' 
                  : job.startDate 
                    ? `Début: ${formatDate(job.startDate)}` 
                    : 'Date de début non précisée'}
              </span>
            </div>
          </div>
        </div>

        {/* Footer with Publication Date & Apply Button */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between pt-2 mt-auto border-t border-gray-100 dark:border-gray-700/50">
          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
            Publié le {formatDate(job.published)}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the card click
              handleCardClick();
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs sm:text-sm font-medium rounded-lg transition-colors order-1 sm:order-2 whitespace-nowrap"
          >
            <span>Voir le détail</span>
            <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}